import { RequestUtil } from '../../../../common/util/request-util';
import { Injectable } from '@angular/core';
import { StationAppBaseBackendService } from '../station-app-base-backend.service';
import { gql } from 'apollo-angular';
import { lastValueFrom } from 'rxjs';
import ObjectUtil from '../../../../common/util/object-util';
import { GraphQLQueryResponse } from '../../../../common/interfaces/graphql-query-response';
import { SearchParam } from '../../../../common/interfaces/search-param';
import { SortParams } from '../../../../common/interfaces/sort-params';
import { ColumnFilter } from '../../../../common/interfaces/column-filter';
import { Configuration } from '../../interfaces/configuration';
import { Unit } from '../../interfaces';


import { ServiceErrorUtil } from '../../../../common/util/service-error.util';
@Injectable({
  providedIn: 'root'
})
export class ConfigurationService extends StationAppBaseBackendService {

  override async get(
    afterCursor: string | null = null,
    searchParams: SearchParam[][] | null = [],
    sortParams: SortParams | null = null,
    columnFilters?: ColumnFilter[] | null,
    otherParams: any | null = null
  ): Promise<GraphQLQueryResponse> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query (
        $first: Int
        $after: String
        $last: Int
        $before: String
        $where: ConfigurationFilterInput
        $order: [ConfigurationSortInput!]
      ) {
        configurations(first: $first, last: $last, after: $after, before: $before, where: $where, order: $order) {
          totalCount
          pageInfo {
            startCursor
            hasPreviousPage
            hasNextPage
            endCursor
          }
          nodes {
            id
            name
            ownerId
            phone
            email
            billingFactor
            smsEnable
            notificationEmails
            notificationsEnabled
            unitId
            unit {
              id
              name
              description
            }
            addressId
            address {
              id
              street1
              street2
              city
              postalCode
              countryId
              districtId
            }
            timeZone
            adminMFAEnabled
            customerMFAEnabled
            hideFinancialDataForOperator
            hideFinancialDataForCustomer
            smsNumbers {
              id
              number
              configurationId
            }
            commServiceSyncIntervalMinutes
          }
        }
      }
    `;

    const queryResponse = await lastValueFrom(
      this.apollo.query<any>({
        variables: {
          first: otherParams?.first ?? 10,
          after: afterCursor ?? null,
          last: null,
          before: null,
          where: this.buildWhere(searchParams, columnFilters),
          order: this.buildOrder(sortParams),
        },
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResponse.data?.configurations) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        'Error getting configurations', requestId
      );
    }

    return {
      nextPageLink: queryResponse.data.configurations.pageInfo.endCursor ?? null,
      startPageLink: queryResponse.data.configurations.pageInfo.startCursor ?? null,
      endPageLink: queryResponse.data.configurations.pageInfo.endCursor ?? null,
      hasPreviousPage: queryResponse.data.configurations.pageInfo.hasPreviousPage ?? false,
      hasNextPage: queryResponse.data.configurations.pageInfo.hasNextPage ?? false,
      totalCount: queryResponse.data.configurations.totalCount,
      data: ObjectUtil.deepCopy(queryResponse.data.configurations.nodes),
    };
  }

  override async getById(id: number): Promise<Configuration> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query ($where: ConfigurationFilterInput) {
        configurations(first: 1, where: $where) {
          nodes {
            id
            name
            ownerId
            phone
            email
            billingFactor
            smsEnable
            notificationEmails
            notificationsEnabled
            unitId
            unit {
              id
              name
              description
            }
            addressId
            address {
              id
              street1
              street2
              city
              postalCode
              countryId
              districtId
            }
            timeZone
            adminMFAEnabled
            customerMFAEnabled
            hideFinancialDataForOperator
            hideFinancialDataForCustomer
            smsNumbers {
              id
              number
              configurationId
            }
            commServiceSyncIntervalMinutes
          }
        }
      }
    `;

    const queryResponse = await lastValueFrom(
      this.apollo.query<any>({
        variables: { where: { id: { eq: id } } },
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResponse.data?.configurations?.nodes?.length) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        'Error getting configuration',
        requestId
      );
    }

    return ObjectUtil.deepCopy(queryResponse.data.configurations.nodes[0]);
  }

  override async create(data: Configuration): Promise<Configuration> {
    const requestId = RequestUtil.generateRequestId();
    const dataCopy = ObjectUtil.deepCopy(data);
    // Remove unit object, only send unitId
    delete dataCopy.unit;
    dataCopy.smsNumbers = dataCopy.smsNumbers?.map(({ configurationId: _, ...rest }: any) => rest);

    const mutation = gql`
      mutation CreateConfiguration($input: CreateConfigurationInput!) {
        createConfiguration(input: $input) {
              id
              name
              ownerId
              phone
              email
              billingFactor
              smsEnable
              notificationEmails
              notificationsEnabled
              unitId
              addressId
              address {
                id
                street1
                street2
                city
                postalCode
                countryId
                districtId
              }
              timeZone
              adminMFAEnabled
              customerMFAEnabled
              hideFinancialDataForOperator
              hideFinancialDataForCustomer
              smsNumbers {
                id
                number
                configurationId
              }
              commServiceSyncIntervalMinutes
        }
      }
    `;

    const queryResult = await lastValueFrom(
      this.apollo.mutate<any>({
        variables: { input: dataCopy },
        mutation,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResult.data?.createConfiguration) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error creating configuration', requestId);
    }

    return queryResult.data.createConfiguration;
  }

  override async update(data: Configuration): Promise<Configuration> {
    const requestId = RequestUtil.generateRequestId();
    const dataCopy = ObjectUtil.deepCopy(data);
    // Remove unit object, only send unitId
    delete dataCopy.unit;
    dataCopy.smsNumbers = dataCopy.smsNumbers?.map(({ configurationId: _, ...rest }: any) => rest);

    const mutation = gql`
      mutation UpdateConfiguration($input: UpdateConfigurationInput!) {
        updateConfiguration(input: $input) {
              id
              name
              ownerId
              phone
              email
              billingFactor
              smsEnable
              notificationEmails
              notificationsEnabled
              unitId
              addressId
              address {
                id
                street1
                street2
                city
                postalCode
                countryId
                districtId
              }
              timeZone
              adminMFAEnabled
              customerMFAEnabled
              hideFinancialDataForOperator
              hideFinancialDataForCustomer
              smsNumbers {
                id
                number
                configurationId
              }
              commServiceSyncIntervalMinutes
        }
      }
    `;

    const queryResult = await lastValueFrom(
      this.apollo.mutate<any>({
        variables: { input: dataCopy },
        mutation,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResult.data?.updateConfiguration) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error updating configuration', requestId);
    }

    return queryResult.data.updateConfiguration;
  }

  override async delete(id: number): Promise<any> {
    const requestId = RequestUtil.generateRequestId();
    const mutation = gql`
      mutation DeleteConfiguration($input: DeleteConfigurationInput!) {
        deleteConfiguration(input: $input)
      }
    `;

    const queryResult = await lastValueFrom(
      this.apollo.mutate<any>({
        variables: { input: { id } },
        mutation,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResult.data?.deleteConfiguration) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error deleting configuration', requestId);
    }

    return queryResult.data.deleteConfiguration;
  }

  override async getValidationData(): Promise<any> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query {
        stationAppValidationQuery(dataType: "configurations")
      }
    `;

    const queryResult = await lastValueFrom(
      this.apollo.query<any>({
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResult.data) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,'Error fetching configuration validation data', requestId);
    }

    return ObjectUtil.normalizeValidationPayload(JSON.parse(queryResult.data.stationAppValidationQuery));
  }

  async getConfiguredUnit(): Promise<Unit[]> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query GetConFiguredUnit {
        conFiguredUnit {
          id
          name
        }
      }
    `;

    const queryResponse = await lastValueFrom(
      this.apollo.query<any>({
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResponse.data?.conFiguredUnit) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        'Error getting units', requestId
      );
    }

    return ObjectUtil.deepCopy(queryResponse.data.conFiguredUnit);
  }

  async getAllUnits(): Promise<Unit[]> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query GetAllUnits {
        allUnits {
          id
          name
          description
        }
      }
    `;

    const queryResponse = await lastValueFrom(
      this.apollo.query<any>({
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      })
    );

    if (!queryResponse.data?.allUnits) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        'Error getting units', requestId
      );
    }

    return ObjectUtil.deepCopy(queryResponse.data.allUnits);
  }
}


